import { useTranslations } from '~/client/hooks/useTranslations'
import { getLocaleSelector } from '~/client/store/config'
import { useSelector } from 'react-redux'
import { useMemo } from 'react'

export type Translations = {
  brandCopy: {
    brandFooter: string
    brandKit: string
    brandLogoAltText: string
    brandToggleLabel: string
    brandToggleOff: string
    brandToggleOn: string
    mobileBrandFooter: string
    mobileSaveBrandFooter: string
    saveBrandFooter: string
    signInButtonText: string
  }
  uploadsCopy: {
    acceptedFormatsMessage: string
    acceptedFormatsMessagePhoto: string
    addImageButton: string
    applyMultipleImages: string
    applySingleImage: string
    assetInUseLabel: string
    backButtonText: string
    changeImageButton: string
    clearPreview: string
    clearSelection: string
    closeButtonText: string
    deleteButtonTitle: string
    deleteConfirmationCancel: string
    deleteConfirmationConfirm: string
    deleteConfirmationDescription: string
    deleteConfirmationHeader: string
    deletingLabel: string
    dismissAlert: string
    genericPreview: string
    generalError: string
    imageUploadButtonText: string
    imageUploadErrorMessage: string
    loadingLabel: string
    loadingText: string
    logInButtonText: string
    logInDescription: string
    modalImageHeaderText: string
    uploadedAssetsTitle: string
    uploadingLabel: string
  }
  modalCopy: {
    filterHeader: string
    filterSubtext: string
    manualInputSubheader: string
    submitButtonText: string
    closeButtonText: string
  }
  cardCopy: {
    filterHeader: string
    filterSubtext: string
    manualInputSubheader: string
  }
  textCopy: {
    textLoading: string
  }
}

export const usePersonalizationTranslations = (imageCopyKey: string): Translations => {
  const localize = useTranslations()
  const locale = useSelector(getLocaleSelector)

  const changeImageButtonKey = useMemo(() => {
    const changeImageKeys = {
      Photo: 'PersonalizationChangePhotoButton',
      Logo: 'PersonalizationChangeLogoButton',
      Image: 'PersonalizationChangeImageButton',
    }

    return changeImageKeys[imageCopyKey as keyof typeof changeImageKeys]
  }, [imageCopyKey])

  const addImageButtonKey = useMemo(() => {
    const shortAddImageKeys = {
      Photo: 'PersonalizationAddPhotoButton',
      Logo: 'PersonalizationAddLogoButton',
      Image: 'PersonalizationAddImageButton',
    }

    const longAddImageKeys = {
      Photo: 'PersonalizationAddYourPhotoButton',
      Logo: 'PersonalizationAddYourLogoButton',
      Image: 'PersonalizationAddYourImageButton',
    }

    if (locale === 'pt-pt') {
      return shortAddImageKeys[imageCopyKey as keyof typeof shortAddImageKeys]
    }

    if (imageCopyKey !== 'Photo' && locale.includes('en-')) {
      return 'PersonalizationAddLogoOrPhotoButton'
    }

    return longAddImageKeys[imageCopyKey as keyof typeof longAddImageKeys]
  }, [imageCopyKey, locale])

  const translations = useMemo(
    () => ({
      brandCopy: {
        brandFooter: localize('PersonalizationBrandFooter'),
        brandKit: localize('BrandKit'),
        brandLogoAltText: localize('BrandLogoAltText'),
        brandToggleLabel: localize('BrandToggleLabel'),
        brandToggleOff: localize('Off'),
        brandToggleOn: localize('On'),
        mobileBrandFooter: localize('PersonalizationMobileBrandFooter'),
        mobileSaveBrandFooter: localize('PersonalizationMobileSaveBrandFooter'),
        saveBrandFooter: localize('PersonalizationSaveBrandFooter'),
        signInButtonText: localize('SignInButtonText'),
      },
      uploadsCopy: {
        acceptedFormatsMessage: localize('PersonalizationModalAcceptedFormats'),
        acceptedFormatsMessagePhoto: localize('PhotoPersonalizationAcceptedFormatsMessage2'),
        addImageButton: localize(addImageButtonKey),
        applyMultipleImages: localize('PersonalizationModalPluralPreviewImageButtonText'),
        applySingleImage: localize('PersonalizationModalSingularPreviewImageButtonText'),
        assetInUseLabel: localize('PhotoPersonalizationImageUsedHoverText'),
        backButtonText: localize('PersonalizationModalBackButtonText'),
        changeImageButton: localize(changeImageButtonKey),
        clearPreview: localize('PersonalizationModalClearPreviewButtonText'),
        clearSelection: localize('ClearSelection'),
        closeButtonText: localize('PhotoPersonalizationModalCloseButtonTitle'),
        deleteButtonTitle: localize('PhotoPersonalizationDeleteImageButton'),
        deleteConfirmationCancel: localize('Cancel'),
        deleteConfirmationConfirm: localize('Confirm'),
        deleteConfirmationDescription: localize('PersonalizationModalDeleteImageConfirmationDescription'),
        deleteConfirmationHeader: localize('PersonalizationModalDeleteImageConfirmationHeader'),
        deletingLabel: localize('PhotoPersonalizationImageSelectionAssetDeletingLabel'),
        dismissAlert: localize('DismissAlert'),
        genericPreview: localize('PersonalizationModalGenericPreviewImageButtonText'),
        generalError: localize('PhotoPersonalizationGeneralError'),
        imageUploadButtonText: localize('PersonalizationModalImageUploadButtonText'),
        imageUploadErrorMessage: localize('PersonalizationImageUploadErrorMessage'),
        loadingLabel: localize('PhotoPersonalizationImageSelectionAssetLoadingLabel'),
        loadingText: localize('Loading'),
        logInButtonText: localize('LogInButtonText'),
        logInDescription: localize('PersonalizationModalLogInDescription'),
        modalImageHeaderText: localize('PersonalizationModalImageHeaderText'),
        uploadedAssetsTitle: localize('PersonalizationModalUploadedAssetsTitle'),
        uploadingLabel: localize('PhotoPersonalizationImageSelectionAssetUploadingLabel'),
      },
      modalCopy: {
        filterHeader: localize('PersonalizationFilterHeader'),
        filterSubtext: localize('PersonalizationFilterSubtext'),
        manualInputSubheader: localize('PersonalizationManualInputSubheader'),
        submitButtonText: localize('PersonalizationSubmitButtonText'),
        closeButtonText: localize('PhotoPersonalizationModalCloseButtonTitle'),
      },
      cardCopy: {
        filterHeader: localize('PersonalizationFilterHeader'),
        filterSubtext: localize('PersonalizationFilterSubtext'),
        manualInputSubheader: localize('PersonalizationManualInputSubheader'),
        textLoading: localize('Loading'),
      },
      textCopy: {
        textLoading: localize('Loading'),
      },
    }),
    [addImageButtonKey, changeImageButtonKey, localize],
  )

  return translations
}
